import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core'; 
import { BehaviorSubject, map, Observable, of } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface UserDto {
  id: string;
  roles: string[];
}

@Injectable({
  providedIn: 'root',
})
export class UserService {

  private currentUserRequest$: Observable<UserDto | null> | null = null;

  constructor(private http: HttpClient) {
    this.loadFromStorage();
  }

  private userNameSubject = new BehaviorSubject<string | null>(null);
  userName$ = this.userNameSubject.asObservable();

  setUserName(name: string | null) {
    this.userNameSubject.next(name);
    if (name) localStorage.setItem('userName', name);
  }

  getUserName(): string | null {
    return this.userNameSubject.getValue();
  }

  // --- Documento ---
  private userDocumentSubject = new BehaviorSubject<string | null>(null);
  userDocument$ = this.userDocumentSubject.asObservable();

  setUserDocument(document: string | null) {
    this.userDocumentSubject.next(document);
    if (document) localStorage.setItem('userDocument', document);
  }

  getUserDocument(): string | null {
    return this.userDocumentSubject.getValue();
  }

  // --- Roles/perfiles ---
  private userRolesSubject = new BehaviorSubject<string[]>([]);
  userRoles$ = this.userRolesSubject.asObservable();

  setUserRoles(roles: string[]) {
    this.userRolesSubject.next(roles);
    localStorage.setItem('userRoles', JSON.stringify(roles));
  }

  getUserRoles(): string[] {
    const roles = this.userRolesSubject.getValue();
    if (!roles || roles.length === 0) {
      const stored = localStorage.getItem('userRoles');
      if (stored) {
        const parsed = JSON.parse(stored);
        this.userRolesSubject.next(parsed);
        return parsed;
      }
    }
    return roles;
  }

  // --- Cargar del localStorage al iniciar ---
  private loadFromStorage() {
    const name = localStorage.getItem('userName');
    if (name) this.userNameSubject.next(name);

    const doc = localStorage.getItem('userDocument');
    if (doc) this.userDocumentSubject.next(doc);

    const roles = localStorage.getItem('userRoles');
    if (roles) this.userRolesSubject.next(JSON.parse(roles));
  }

  // --- Llamada al backend ---
  getUserByDocument(documentNumber: string): Observable<UserDto | null> {
    const url = `${environment.urlCore}/security/user/current`;

    if (this.currentUserRequest$) return this.currentUserRequest$;

    this.currentUserRequest$ = this.http.get<UserDto | null>(url).pipe(
      map(user => user ?? null),
      shareReplay(1),
      catchError(() => {
        this.currentUserRequest$ = null;
        return of(null);
      })
    );

    return this.currentUserRequest$;
  }

  loadRolesFromBackend(documentNumber: string) {
    return this.getUserByDocument(documentNumber).pipe(
      map(user => {
        if (user?.roles) {
          this.setUserRoles(user.roles); // actualiza BehaviorSubject y localStorage
          return user.roles;
        }
        return [];
      })
    );
  }
}
